import * as React from 'react'

import { styled } from '~/styled'

import { FileDropContext } from './FileDropContext'
import { type FileDropContextValue } from './types'

interface FileDropProps extends Omit<
  React.ComponentProps<'div'>,
  'onDrop' | 'ref' | 'children'
> {
  onDrop: (files: File[]) => void
  children?:
    | React.ReactNode
    | ((value: FileDropContextValue) => React.ReactNode)
  accept?: React.InputHTMLAttributes<HTMLInputElement>['accept']
  multiple?: React.InputHTMLAttributes<HTMLInputElement>['multiple']
}

const StyledContainer = styled('div', {
  base: [
    'flex',
    'flex-col',
    'gap-6',
    'items-center',
    'border',
    'border-dashed',
    'border-grey-500',
    'px-12',
    'py-8',
    'rounded-md',
    'cursor-pointer',
    '**:pointer-events-none'
  ]
})

export const FileDrop = ({
  children,
  accept,
  multiple,
  onDrop,
  ...props
}: FileDropProps) => {
  const [isDragging, setIsDragging] = React.useState(false)
  const [files, setFiles] = React.useState<File[]>([])
  const fileUploadInputRef = React.useRef<HTMLInputElement>(null)

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const handleDrop = (fileList: FileList | null) => {
    if (!fileList) return

    setIsDragging(false)

    const files = Array.from(fileList)
    onDrop(files)
    setFiles(files)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (['Space', 'Enter'].includes(event.key)) {
      fileUploadInputRef.current?.click()
    }
  }

  const value: FileDropContextValue = { isDragging, files }

  return (
    <FileDropContext.Provider value={value}>
      <StyledContainer
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(event) => {
          event.preventDefault()
          handleDrop(event.dataTransfer.files)
        }}
        onClick={() => fileUploadInputRef.current?.click()}
        role="button"
        onKeyDown={handleKeyDown}
        tabIndex={0}
        {...props}
      >
        {typeof children === 'function' ? children(value) : children}
        <input
          type="file"
          ref={fileUploadInputRef}
          style={{ visibility: 'hidden' }}
          accept={accept}
          multiple={multiple}
          onChange={(event) => {
            handleDrop(event.target.files)
          }}
        />
      </StyledContainer>
    </FileDropContext.Provider>
  )
}

FileDrop.displayName = 'FileDrop'
